import { useEffect, useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Combobox, InputDate } from 'medere-ui-framework';

import { CashRegisterFinancialAccountDTO } from '../../../dto/locationcash/LocationCashDTO';
import { MovementResponseDTO } from '../../../dto/FinancialAccountDTO';
import { UsersListItemDTO } from '../../../dto/users/UsersDTO';
import { getCashSummary, getCashRegisterFinancialAccounts } from '../../../services/LocationCashController';
import { listMovements } from '../../../services/FinancialAccountController';

import { useLocationDeskContext } from '../context/LocationDeskContext';
import { formatDateTime, formatMoney, dateParam, startOfDay, endOfDay } from '../utils';
import { getUsersList } from '../../../servicesHooks/Users/useUsersService';

const PAGE_SIZE = 20;

export default function MovementsHistoryView() {
    const { movementsLocationId, returnFromMovements } = useLocationDeskContext();

    const [cashRegisterId, setCashRegisterId] = useState<number | null>(null);
    const [locationDescription, setLocationDescription] = useState('');
    const [loadingInit, setLoadingInit] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [movements, setMovements] = useState<MovementResponseDTO[]>([]);
    const [total, setTotal] = useState(0);
    const [first, setFirst] = useState(0);
    const [loading, setLoading] = useState(false);

    const [fromDate, setFromDate] = useState<Date>(() => startOfDay(new Date()));
    const [toDate, setToDate] = useState<Date>(() => new Date());

    // Filtros
    const [accounts, setAccounts] = useState<CashRegisterFinancialAccountDTO[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<CashRegisterFinancialAccountDTO | null>(null);
    const [users, setUsers] = useState<UsersListItemDTO[]>([]);
    const [selectedUser, setSelectedUser] = useState<UsersListItemDTO | null>(null);

    // Obtener cashRegisterId, cuentas y usuarios al montar
    useEffect(() => {
        if (!movementsLocationId) return;
        setLoadingInit(true);
        setError(null);

        getCashSummary(movementsLocationId)
            .then((summary) => {
                setCashRegisterId(summary.locationCashRegisterId);
                setLocationDescription(summary.description);
                return getCashRegisterFinancialAccounts(summary.locationCashRegisterId);
            })
            .then((accs) => setAccounts(accs.filter(a => a.active)))
            .catch(() => {
                setError('No se pudo obtener la información de la caja.');
                setCashRegisterId(null);
            })
            .finally(() => setLoadingInit(false));

        getUsersList(0, 200)
            .then(setUsers)
            .catch(console.error);
    }, [movementsLocationId]);

    // Cargar página de movimientos
    const loadPage = useCallback((offset: number) => {
        if (!cashRegisterId) return;
        setLoading(true);
        listMovements({
            cashRegisterId,
            from: dateParam(startOfDay(fromDate)),
            to: dateParam(endOfDay(toDate)),
            financialAccountId: selectedAccount?.financialAccountId,
            userId: selectedUser?.userId,
            offset,
            limit: PAGE_SIZE,
        })
            .then(res => {
                setMovements(res.items);
                setTotal(res.totalCount);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [cashRegisterId, fromDate, toDate, selectedAccount, selectedUser]);

    // Cargar al obtener cashRegisterId o cambiar filtros
    useEffect(() => {
        if (!cashRegisterId) return;
        setFirst(0);
        loadPage(0);
    }, [cashRegisterId, loadPage]);

    const onPage = (e: DataTablePageEvent) => {
        setFirst(e.first);
        loadPage(e.first);
    };

    if (loadingInit) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <Button
                    label="Volver"
                    icon="pi pi-arrow-left"
                    className="buttonMain3 mb-3"
                    onClick={returnFromMovements}
                />
                <p style={{ color: '#DC3545' }}>{error}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="active-desk-header">
                <div className="flex align-items-center gap-3">
                    <Button
                        icon="pi pi-arrow-left"
                        className="buttonMain3"
                        tooltip="Volver"
                        tooltipOptions={{ position: 'top' }}
                        onClick={returnFromMovements}
                    />
                    <div>
                        <h1 className="active-desk-title">Historial de movimientos</h1>
                        {locationDescription && (
                            <div className="active-desk-meta">
                                <span>
                                    <i className="pi pi-dollar" />
                                    {locationDescription}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex align-items-end gap-4 mb-3 flex-wrap">
                <div style={{ width: '180px' }}>
                    <InputDate
                        label="Desde"
                        value={fromDate}
                        onChange={(e: any) => setFromDate(e.value ?? startOfDay(new Date()))}
                    />
                </div>
                <div style={{ width: '180px' }}>
                    <InputDate
                        label="Hasta"
                        value={toDate}
                        onChange={(e: any) => setToDate(e.value ?? new Date())}
                    />
                </div>
                <div style={{ minWidth: '180px' }}>
                    <Combobox
                        label="Cuenta"
                        list={accounts}
                        value={selectedAccount}
                        setValue={(v: CashRegisterFinancialAccountDTO | null) => setSelectedAccount(v)}
                        optionLabel="financialAccountName"
                        placeholder="Todas"
                    />
                </div>
                <div style={{ minWidth: '180px' }}>
                    <Combobox
                        label="Usuario"
                        list={users}
                        value={selectedUser}
                        setValue={(v: UsersListItemDTO | null) => setSelectedUser(v)}
                        optionLabel="username"
                        placeholder="Todos"
                    />
                </div>
            </div>

            <DataTable
                value={movements}
                size="small"
                stripedRows
                lazy
                paginator
                rows={PAGE_SIZE}
                first={first}
                totalRecords={total}
                onPage={onPage}
                loading={loading}
                emptyMessage="No hay movimientos para el rango seleccionado."
            >
                <Column field="dateTime" header="Fecha/Hora" body={(row: MovementResponseDTO) => formatDateTime(row.dateTime)} style={{ width: '160px' }} />
                <Column field="username" header="Usuario" body={(row: MovementResponseDTO) => row.username ?? '—'} style={{ width: '120px' }} />
                <Column field="financialAccountName" header="Cuenta" style={{ width: '150px' }} />
                <Column field="movementTypeDescription" header="Tipo" style={{ width: '140px' }} />
                <Column field="notes" header="Notas" body={(row: MovementResponseDTO) => row.notes ?? '—'} style={{ minWidth: '180px' }} />
                <Column
                    field="amount"
                    header="Monto"
                    body={(row: MovementResponseDTO) => (
                        <span className="font-semibold" style={row.amount < 0 ? { color: '#DC3545' } : undefined}>
                            {formatMoney(row.amount)}
                        </span>
                    )}
                    style={{ width: '130px', textAlign: 'right' }}
                />
            </DataTable>
        </div>
    );
}
